/**
 * Responsibility: Implement npc guard behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
// AI prompts are the primary protection layer.
// This runtime guard is intentionally a narrow fail-safe for obvious leaks only.
const EXACT_BLOCKED_META_IDENTIFIERS = new Set([
  'narrator',
  'supervisor',
  'game master',
  'gamemaster',
  'dungeon master',
  'dungeonmaster',
  'gm',
  'dm',
  'logic forge',   // known internal agent label
  'reality forge', // known internal agent label
  'world simulator',
  'world sim',
])

function normalizeNpcNameKey(name: unknown): string {
  return String(name ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[`"'.,:;!?()[\]{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^the\s+/, '')
}

function hasMetaCue(tokens: string[]): boolean {
  return tokens.some(token => (
    token === 'system'
    || token === 'meta'
    || token === 'engine'
    || token === 'ai'
    || token === 'assistant'
    || token === 'model'
    || token === 'prompt'
  ))
}

function hasRoleCue(tokens: string[]): boolean {
  if (tokens.includes('narrator') || tokens.includes('supervisor')) return true
  if (tokens.includes('gm') || tokens.includes('dm')) return true
  if (tokens.includes('game') && tokens.includes('master')) return true
  if (tokens.includes('dungeon') && tokens.includes('master')) return true
  if (tokens.includes('logic') && tokens.includes('forge')) return true
  if (tokens.includes('reality') && tokens.includes('forge')) return true
  if (tokens.includes('world') && (tokens.includes('sim') || tokens.includes('simulator'))) return true
  return false
}

export function isMetaSystemNpcName(name: unknown): boolean {
  const key = normalizeNpcNameKey(name)
  if (!key) return false
  if (EXACT_BLOCKED_META_IDENTIFIERS.has(key)) return true

  const tokens = key.split(' ').filter(Boolean)
  if (!tokens.length) return false

  if (hasRoleCue(tokens) && hasMetaCue(tokens)) return true
  return false
}
